import { Box, Chip, IconButton, Link as MuiLink, Typography } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import { Download } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatDocDate } from "./dateFormat.js";
import { articleDate } from "./folder-filters.js";
import { docTypeIcon } from "./icons.js";
import { kbArticlePath } from "./kb-paths.js";
import type { DocSummary } from "./types.js";

/**
 * ONE article row for every list in the Documentation section — a folder's
 * articles, the search results, Featured and Recently added on the landing
 * page. Type icon, title, a one-line summary, the date; optionally the
 * folder and version chips (search, where a row can come from anywhere) and
 * a Download PDF button (the folder page).
 *
 * There is no status chip anywhere on it. Everything a reseller can reach is
 * published, and a chip that always reads the same is decoration. There is
 * also no "Uncategorized" or "—" placeholder: a row shows what it has.
 */
export function ArticleRow({
  doc,
  showFolder = false,
  showVersion = false,
  onPdf,
  pdfBusy = false,
}: {
  doc: DocSummary;
  /** Name the folder — for lists that mix folders (search, the landing page). */
  showFolder?: boolean;
  /** Show `v10.2` when the article is release documentation (search only:
   * every other list is library-shelf by construction). */
  showVersion?: boolean;
  /** Offer a per-row "Download PDF"; the folder page passes it. */
  onPdf?: () => void;
  pdfBusy?: boolean;
}): JSX.Element {
  const Icon = docTypeIcon(doc.doc_type);
  const date = formatDocDate(articleDate(doc));

  return (
    <Box
      component="li"
      data-testid={`kb-row-${doc.id}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
        "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.6) },
      }}
    >
      <Box sx={{ display: "inline-flex", color: "primary.main", flexShrink: 0 }} aria-hidden>
        <Icon size={18} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1, display: "grid", gap: 0.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
          <MuiLink
            component={RouterLink}
            to={kbArticlePath(doc.slug)}
            underline="hover"
            sx={{ fontSize: 14, fontWeight: 600, color: "text.primary", minWidth: 0 }}
          >
            {doc.title}
          </MuiLink>
          {showFolder && doc.category && <MetaChip>{doc.category.name}</MetaChip>}
          {showVersion && doc.default_release && (
            <MetaChip testId={`kb-row-version-${doc.id}`}>v{doc.default_release.version}</MetaChip>
          )}
          {doc.featured && <MetaChip tone="warn">Featured</MetaChip>}
        </Box>
        {doc.summary && (
          <Typography noWrap sx={{ fontSize: 13, color: "text.secondary" }}>
            {doc.summary}
          </Typography>
        )}
      </Box>

      {date && (
        <Box
          data-testid={`kb-row-date-${doc.id}`}
          sx={{
            flexShrink: 0,
            fontSize: 12,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
            display: { xs: "none", sm: "block" },
          }}
        >
          {date}
        </Box>
      )}

      {onPdf && (
        <IconButton
          size="small"
          aria-label={`Download ${doc.title} as PDF`}
          data-testid={`kb-row-pdf-${doc.id}`}
          disabled={pdfBusy}
          onClick={onPdf}
          sx={{ flexShrink: 0 }}
        >
          <Download size={16} />
        </IconButton>
      )}
    </Box>
  );
}

/** The bordered card every article list sits in. */
export function ArticleList({ children, testId }: { children: ReactNode; testId: string }): JSX.Element {
  return (
    <Box
      component="ul"
      data-testid={testId}
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        overflow: "hidden",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
      }}
    >
      {children}
    </Box>
  );
}

/** Small inline pill: the folder, the version, the Featured marker. */
function MetaChip({
  tone = "neutral",
  testId,
  children,
}: {
  tone?: "neutral" | "warn";
  testId?: string;
  children: ReactNode;
}): JSX.Element {
  const toneSx: SxProps<Theme> =
    tone === "warn"
      ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.15), color: "warning.main" }
      : { bgcolor: "surfaceSoft", color: "text.secondary" };
  return (
    <Chip
      label={children}
      size="small"
      {...(testId ? { "data-testid": testId } : {})}
      sx={[{ height: 20, borderRadius: 0.75, fontSize: 11, "& .MuiChip-label": { px: 0.75 } }, toneSx]}
    />
  );
}
